<template>
	<transition name="modal">
		<div id="seabags-configurator" @initialize-configurator="getInitializationData" aria-modal="true" v-show="configuratorOpen">
			<div class="seabags-configurator-window content">
				<button
					id="visualizer--close"
					:data-current-step="currentStep"
					@click="closeConfigurator"
					class="close-configurator"
					aria-label="Close"
					title="Close"
				>
					<span class="close-icon"></span>
				</button>
				<template v-if="loading">
					<div class="loader">
						<span class="loading-icon"></span>
						<br />
						<h2>Loading...</h2>
					</div>
				</template>
				<template v-if="error">
					<div class="error-window">
						<h2>Oops! Something went wrong!</h2>
						<p>Please try again later.</p>
						<p v-if="error.code">Error code: {{ error.code }}</p>
						<p>
							<button @click="closeConfigurator">Close</button>
						</p>
					</div>
				</template>
				<template v-if="productLoaded && !loading && !error">
					<div class="columns">
						<div class="sticky-subtotal">{{ subtotalFormatted }}</div>
						<div class="col-left">
							<Preview :preview="preview" v-for="preview in previews" :key="preview[0]" />
						</div>
						<div class="col-right">
							<div class="configurations">
								<Steps />
							</div>
						</div>
					</div>
				</template>
			</div>
		</div>
	</transition>
</template>

<script>
import helper from "@/mixins/helper.js";
import GTM from "@/mixins/gtm";
import { mapState } from "vuex";
import Preview from "@/components/Preview";
// import ProgressBar from "@/components/ProgressBar"; REMOVING PROGRESS BAR 20220315
import Steps from "@/components/Steps";

export default {
	name: "app",
	data: function() {
		return {
			magentoStatus: "",
			configurationLoading: false,
			productLoaded: false,
			magentoData: null
		};
	},
	mixins: [GTM, helper],
	components: {
		Preview,
		// ProgressBar, REMOVING PROGRESS BAR 20220315
		Steps
	},
	mounted() {
		if (process.env.NODE_ENV !== "production") {
			this.getInitializationData();
		}
	},
	methods: {
		openConfigurator() {
			this.$store.dispatch("configurator/toggleConfigurator", true);
		},
		closeConfigurator() {
			this.$store.dispatch("configurator/toggleConfigurator", false);
		},
		getInitializationData(event) {
			const payload = event ? event.detail : null;

			if (!payload) {
				return;
			}

			this.magentoStatus = payload.status || "";

			if (this.magentoStatus === "magento-data-loading") {
				this.openConfigurator();
			} else if (this.magentoStatus === "magento-data-loading-failed") {
				this.setError(724, "Could not load data from Magento.");
			} else if (this.magentoStatus === "magento-data-loaded") {
				// Init config
				if (payload.data) {
					this.magentoData = payload.data;
				}

				if (this.magentoData.sku) {
					this.setInitializationDataToStore();
					this.selectProduct(this.magentoData.sku);
					this.openConfigurator();
				} else {
					console.log("Configurator not initialized from Sales Force. Configurator running in test-mode.");
				}
			}
		},
		setInitializationDataToStore() {
			// Set Magento data to store
			this.$store.dispatch("configurator/setMagentoData", this.magentoData);
		},
		selectProduct(file) {
			this.getConfiguratorData(file);
		},
		getConfiguratorData: function(file) {
			const fileName = file || "configurator";
			this.configurationLoading = true;
			this.$http
				.get(process.env.VUE_APP_ROOT_API + fileName + ".json")
				.then(function(response) {
					if (!response.error && response.data) {
						this.$store.dispatch("configurator/setConfig", response.data);
						// Set first step
						if (response.data.steps) {
							this.$store.dispatch("configurator/setCurrentStep", response.data.steps[0].id);
						}
						this.productLoaded = true;
					} else {
						this.setError(635, "Fetching configurator data failed!");
						console.error("Fetching configurator data failed!");
					}
					this.configurationLoading = false;
				})
				.catch(function(e) {
					this.configurationLoading = false;
					this.setError(634, "Fetching configurator data failed!");
					console.error(e, "Fetching configurator data failed: ", e.url, e.statusText, "error: ", e.status);
				});
		}
	},
	computed: {
		loading() {
			return this.magentoStatus === "magento-data-loading" || this.configurationLoading;
		},
		price() {
			return this.config.product.price ? this.formatPrice(this.config.product.price) : "-";
		},
		previews() {
			return this.config.previews ? this.config.previews : [];
		},
		subtotalFormatted: function() {
			return this.formatPrice(this.$store.getters["configurator/subtotal"]);
		},
		...mapState({
			config: state => state.configurator.config,
			error: state => state.configurator.error,
			configuratorOpen: state => state.configurator.configuratorOpen,
			currentStep: state => state.configurator.currentStep
		})
	}
};
</script>

<style lang="scss">
@font-face {
	font-family: "AthleticoSharp";
	src: url(./styles/fonts/athleticosharp_regular_macroman/AthleticoSharp-webfont.woff2) format("woff2"),
		url(./styles/fonts/athleticosharp_regular_macroman/AthleticoSharp-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}
@font-face {
	font-family: "averta-extrabold";
	src: url(./styles/fonts/averta_extrabold_macroman/averta-extrabold-webfont.woff2) format("woff2"),
		url(./styles/fonts/averta_extrabold_macroman/averta-extrabold-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}
@font-face {
	font-family: "radcliffe-bold";
	src: url(./styles/fonts/radcliffe_bold_macroman/radcliffe-bold-webfont.woff2) format("woff2"),
		url(./styles/fonts/radcliffe_bold_macroman/radcliffe-bold-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}
@font-face {
	font-family: "radcliffe-regular";
	src: url(./styles/fonts/radcliffe_regular_macroman/radcliffe-regular-webfont.woff2) format("woff2"),
		url(./styles/fonts/radcliffe_regular_macroman/radcliffe-regular-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}
@font-face {
	font-family: "stash-bd";
	src: url(./styles/fonts/stash_bold_macroman/stash-bd-webfont.woff2) format("woff2"),
		url(./styles/fonts/stash_bold_macroman/stash-bd-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}

@font-face {
	font-family: proxima nova;
	src: url(./styles/fonts/proxima-nova/2E3394_0_0.woff2) format("woff2"), url(./styles/fonts/proxima-nova/2E3394_0_0.woff) format("woff");
	font-weight: 300;
	font-style: normal;
}

@font-face {
	font-family: proxima nova;
	src: url(./styles/fonts/proxima-nova/2E3394_1_0.woff2) format("woff2"), url(./styles/fonts/proxima-nova/2E3394_1_0.woff) format("woff");
	font-weight: 400;
	font-style: normal;
}

@font-face {
	font-family: proxima nova;
	src: url(./styles/fonts/proxima-nova/2E3394_2_0.woff2) format("woff2"), url(./styles/fonts/proxima-nova/2E3394_2_0.woff) format("woff");
	font-weight: 600;
	font-style: normal;
}

@font-face {
	font-family: proxima nova;
	src: url(./styles/fonts/proxima-nova/2E3394_3_0.woff2) format("woff2"), url(./styles/fonts/proxima-nova/2E3394_3_0.woff) format("woff");
	font-weight: 700;
	font-style: normal;
}

@font-face {
	font-family: "LIGstandard";
	src: url(./styles/fonts/lig_standard/ligstandard-webfont.woff2) format("woff2"),
		url(./styles/fonts/lig_standard/ligstandard-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}

@font-face {
	font-family: "LIGheavy";
	src: url(./styles/fonts/lig_heavy/ligheavycaps-webfont.woff2) format("woff2"),
		url(./styles/fonts/lig_heavy/ligheavycaps-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}

@font-face {
	font-family: "allison-regular";
	src: url(./styles/fonts/allison-regular/allison-regular-webfont.woff2) format("woff2"),
		url(./styles/fonts/allison-regular/allison-regular-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}

@font-face {
	font-family: "librebaskerville-regular";
	src: url(./styles/fonts/librebaskerville-regular/librebaskerville-regular-webfont.woff2) format("woff2"),
		url(./styles/fonts/librebaskerville-regular/librebaskerville-regular-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}

@font-face {
	font-family: "luxuriousscript-regular";
	src: url(./styles/fonts/luxuriousscript-regular/luxuriousscript-regular-webfont.woff2) format("woff2"),
		url(./styles/fonts/luxuriousscript-regular/luxuriousscript-regular-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}

@font-face {
	font-family: "robotocondensed-bold";
	src: url(./styles/fonts/robotocondensed-bold/robotocondensed-bold-webfont.woff2) format("woff2"),
		url(./styles/fonts/robotocondensed-bold/robotocondensed-bold-webfont.woff) format("woff");
	font-weight: normal;
	font-style: normal;
}

.product-selection {
	text-align: center;

	button {
		font-size: 1em;
		margin: 16px;
		padding: 16px;
	}
}

body.visualizer-no-scroll {
	overflow: hidden;

	// Hide Magento's product image magnifier when visualizer is open
	.magnifier-preview {
		display: none;
	}
}

#seabags-configurator {
	font-family: $font-family;
	font-size: $font-size;
	color: $text-color;
	position: fixed;
	top: 0;
	left: 0;
	height: 100%;
	width: 100%;
	z-index: 9999;
	overflow-y: auto;
	overflow-x: hidden;
	background: #fff;
	box-sizing: border-box;

	&.modal-enter {
		.seabags-configurator-window.content {
			transform: translate(0, 100px);
		}
	}
	&.modal-leave-to {
		.seabags-configurator-window.content {
			transform: translate(0, 100px);
		}
	}
	&.modal-enter,
	&.modal-leave-to {
		opacity: 0;
	}

	&.modal-leave,
	&.modal-enter-to {
		opacity: 1;
		.seabags-configurator-window.content {
			transform: none;
		}
	}

	&.modal-enter-active,
	&.modal-leave-active {
		transition: opacity 1000ms, transform 1000ms;
		.seabags-configurator-window.content {
			transition: transform 1000ms;
		}
	}

	* {
		box-sizing: border-box;
	}

	.seabags-configurator-window.content {
		background-color: $background-color;
		width: 100%;
		height: 100%;
		margin: 0 auto;
		position: relative;

		.close-configurator {
			position: absolute;
			top: 12px;
			right: 12px;
			width: 40px;
			height: 40px;
			z-index: 20;
			padding: 0;
			text-align: center;
			overflow: hidden;
			box-shadow: none;
			outline: none;

			.close-icon {
				&:before,
				&:after {
					position: absolute;
					left: 19px;
					top: 7px;
					content: " ";
					color: transparent;
					height: 26px;
					width: 2px;
					background-color: #fff;
					transform: rotate(-45deg);
				}
				&:after {
					transform: rotate(45deg);
				}
			}

			&:focus {
				background: $color-martinique-blue;
				color: #fff;
				-webkit-text-fill-color: #fff; // all:unset changes the text color on Safari
			}
		}

		.configurations {
			position: relative;
		}

		.loader {
			background-color: $background-color;
			text-align: center;
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);

			.loading-icon {
				width: 70px;
				height: 70px;
				display: inline-block;
				background: url("/images/anchor.svg");
				background-size: contain;
				background-position: center center;
				background-repeat: no-repeat;
				-webkit-animation-name: loading;
				-webkit-animation-duration: 2s;
				animation-name: loading;
				animation-duration: 2s;
				animation-iteration-count: infinite;
			}

			@-webkit-keyframes loading {
				from {
					transform: rotateY(0deg);
				}
				to {
					transform: rotateY(-360deg);
				}
			}

			@keyframes loading {
				from {
					transform: rotateY(0deg);
				}
				to {
					transform: rotateY(-360deg);
				}
			}
		}

		.error-window {
			background-color: $background-color;
			width: 50%;
			min-width: 400px;
			position: absolute;
			top: 50%;
			left: 50%;
			text-align: center;
			transform: translate(-50%, -50%);
		}

		.columns {
			all: unset;
			display: flex;
			flex-direction: column;
			height: 100%;
			.sticky-subtotal {
				padding: 5px 8px 2px;
				background: $color-martinique-blue;
				color: #fff;
				-webkit-text-fill-color: #fff; // all:unset changes the text color on Safari
				position: absolute;
				top: 13px;
				left: 0;
				z-index: 2;
			}
			.col-left {
				height: calc(100vh - 350px - 2vw);
				width: calc(100vh - 350px - 2vw);
				position: relative;
				min-width: 1px;
				min-height: 1px;
				max-width: 100%;
				max-height: 100vw;
				margin: 0 auto;
				flex: 0 0 auto;
			}

			.col-right {
				//height: 60vh;
				//overflow-x: hidden;
				flex: 1;
			}
		}
	}

	.product-name {
		font-size: 2em;
		margin-bottom: 0;
	}

	.product-price {
		font-weight: bold;
		font-size: 1.5em;
		margin: 1em 0;
	}

	.option {
		input[type="text"] {
			width: 100%;
			max-width: 300px;
			text-align: center;
		}

		.monogram input {
			height: 50px;
			width: 90px;
			font-weight: bold;
			font-size: 32px;
			text-align: center;
		}
	}

	input[type="text"],
	input[type="number"],
	textarea {
		border: 1px solid $border-color;
		border-radius: 0;
		background: #fff;
		font-family: $font-family;
		font-size: 18px;

		&:focus {
			border-color: $color-martinique-blue;
			box-shadow: none;
			outline: none;
		}
	}

	button {
		font-style: normal;
		-webkit-box-direction: normal;
		text-align: center;
		background-image: none;
		background: $color-martinique-blue;
		border: none;
		color: #fff;
		-webkit-text-fill-color: #fff; // all:unset changes the text color on Safari
		cursor: pointer;
		font-family: $font-family;
		padding: 15px 17px 13px;
		box-sizing: border-box;
		vertical-align: middle;
		font-weight: 400;
		display: inline-block;
		text-decoration: none;
		letter-spacing: 0.2em;
		text-transform: uppercase;
		font-size: 12px;
		line-height: 1.33;
		user-select: none;

		&:hover {
			background: $color-cinder-black;
			cursor: pointer;
		}
		&:disabled {
			opacity: 0.5;
			cursor: default;
		}

		&.button-secondary {
			background: #fff;
			color: $color-martinique-blue;
			-webkit-text-fill-color: $color-martinique-blue; // all:unset changes the text color on Safari

			&:hover {
				background: #fff;
				color: $color-cinder-black;
				-webkit-text-fill-color: $color-cinder-black; // all:unset changes the text color on Safari
			}
		}
	}

	@media only screen and (min-width: $viewport-desktop) {
		.seabags-configurator-window.content {
			position: relative;
			max-width: 1600px;

			.close-configurator {
				top: 20px;
				right: 20px;
				width: 40px;
				height: 40px;
			}

			.columns {
				flex-direction: row;
				.sticky-subtotal {
					padding: 5px 8px 2px;
					font-size: 28px;
					top: 20px;
				}
				.col-left {
					height: auto;
					width: 60%;
					max-width: 1000px;
					max-height: 1000px;
					flex: 0 1 auto;
					border: 16px solid #fff;
				}
				.col-right {
					width: 40%;
					min-width: 500px;
					position: relative;
					overflow-x: hidden;
					flex: 0 1 auto;
					.configurations {
						margin: 0 auto 0 0;
						overflow: hidden;
						height: 100%;
						width: 500px;
						border-left: 1px solid $border-color;
						border-right: 1px solid $border-color;
					}
				}
			}
		}
	}
}
</style>
